// Client Internal Audit — OFFICE job page (/jobs/:id for internal audit jobs; JobRoute picks this page). Separate
// from the bizSAFE JobDetail on purpose: no CT17, COI, client acknowledgement, WSHC or bizSAFE expiry here.
//
// Two tabs, as bizSAFE: "Plan & Prepare" and "Audit & Report".
// Plan (dummy-proof): audit site (never pre-filled; "Copy HQ address"), man-days, auditors, one line per audit day
// (date · [Full day] [Morning] [Afternoon] · start) + "Add another day", site inspection tick-box. The CCIS standard
// plan is worked out (a recurring client starts from last audit's plan); "Edit plan items" is hidden by default.
// Save draft / Send audit plan as bizSAFE (Send needs a saved plan) → job Scheduled, client email copied to the
// clipboard (paste in Outlook), auditors emailed automatically (no COI).
// Report: payment, reports list, return to auditor, resend links, close-out not needed — all via ia-actions.
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { emailHtml, emailDate } from '../lib/emailHtml'
import { toast, failed } from '../lib/toast'
import { cancelContract, reopenContract, lastCancelNote } from '../lib/contractCancel'
import { oneLine } from '../lib/oneLine'
import { iaStandardOptions, standardsLabel, iaJobStatus, sgToday } from '../lib/service'
import { PARTS, PLAN_VERSION, newDay, newCloseoutDay, newPlan, planDays, dayTimes, dayEnd, defaultPlanItems, withSiteWalk, unplannedStandards, sameStandards } from '../lib/iaPlan'
import { KIND_LABEL, REPORT_STATUS, iaAction, pickChecklists } from '../lib/iaReport'
import IAPlanDocument from '../components/IAPlanDocument'
import IAContactLine, { iaContactOf } from '../components/IAContactLine'
import IAGroupLine from '../components/IAGroupLine'

const fmtDT = d => { try { return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) } catch { return '—' } }
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2)) + Date.now().toString(36)
const today = () => sgToday()
const approved = a => a.approval_status === 'Approved'
  && !(a.wsh_auditor_expiry && a.wsh_auditor_expiry < today())
  && !(a.wsho_status && a.wsho_licence_expiry && a.wsho_licence_expiry < today())

// Client plan email (copied to the clipboard as formatted HTML + plain text).
// when: one line per audit day, e.g. ['15 Oct 2026, 9.00am – 5.30pm', '16 Oct 2026, 9.00am – 12.30pm'].
function planEmail({ greet, when, location, auditors, planUrl }) {
  const esc = s => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const p = 'margin:0;line-height:1.5', gap = `<p style="${p}">&nbsp;</p>`
  const html = `<div style="font-family:Calibri,'Segoe UI',Arial,sans-serif;font-size:11pt;line-height:1.5;color:#1f1f1f">
<p style="${p}">Dear ${esc(greet)},</p>${gap}
<p style="${p}">Thank you for engaging CCIS for your internal audit. Please find the audit details below:</p>${gap}
<p style="${p}"><b>Audit Date &amp; Time:</b> ${when.length > 1 ? when.map((w, i) => `<br>&nbsp;&nbsp;Day ${i + 1}: ${esc(w)}`).join('') : esc(when[0])}<br><b>Audit Location:</b> ${esc(location)}<br><b>Auditor(s):</b> ${esc(auditors)}<br><b>Audit Plan:</b> <a href="${esc(planUrl)}">${esc(planUrl)}</a></p>${gap}
<p style="${p}">Kindly arrange for top management, the management representative and the functional heads to be available as per the audit plan, and have your documented information and records ready for review.</p>${gap}
<p style="${p}">Please kindly ensure that the following arrangements are made in advance:</p>
<ul style="margin:4px 0 0 0;padding-left:22px;line-height:1.5">
<li>Set up and testing of the projector or TV (please inform us in advance if either is unavailable)</li>
<li>Reliable Wi-Fi connection</li>
<li>Laptop (backup)</li>
</ul>${gap}
<p style="${p}">Please kindly note that if our trainer/consultant/auditor is already on the way to your location on the day of the session/training, a $50 transportation/administrative fee will be applied regardless of postponement reason.</p>${gap}
<p style="${p}">Thank you.</p></div>`
  const text = `Dear ${greet},\n\nThank you for engaging CCIS for your internal audit. Please find the audit details below:\n\nAudit Date & Time: ${when.length > 1 ? when.map((w, i) => `\n  Day ${i + 1}: ${w}`).join('') : when[0]}\nAudit Location: ${location}\nAuditor(s): ${auditors}\nAudit Plan: ${planUrl}\n\nKindly arrange for top management, the management representative and the functional heads to be available as per the audit plan, and have your documented information and records ready for review.\n\nPlease kindly ensure that the following arrangements are made in advance:\n• Set up and testing of the projector or TV (please inform us in advance if either is unavailable)\n• Reliable Wi-Fi connection\n• Laptop (backup)\n\nPlease kindly note that if our trainer/consultant/auditor is already on the way to your location on the day of the session/training, a $50 transportation/administrative fee will be applied regardless of postponement reason.\n\nThank you.`
  return { html, text }
}

export default function IAJobDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, logAction } = useAuth()
  const [job, setJob] = useState(null)
  const [cycle, setCycle] = useState(null)
  const [cycles, setCycles] = useState([])
  const [auditors, setAuditors] = useState([])
  const [reports, setReports] = useState([])
  const [checklists, setChecklists] = useState(null)   // the checklist library (null until loaded) — integrated-audit check
  const [loading, setLoading] = useState(true)
  const [f, setF] = useState(null)          // editable fields
  const [saveState, setSaveState] = useState('')
  const [editItems, setEditItems] = useState(false)
  const [planOpen, setPlanOpen] = useState(false)   // audit plan preview folded by default
  const [editStd, setEditStd] = useState(false)
  const [stdOpts, setStdOpts] = useState([])   // tick-box list, fixed while editing (keeps an unticked "other" standard)
  const [busy, setBusy] = useState('')
  const [returning, setReturning] = useState(false)
  const [payModal, setPayModal] = useState(false)      // payment-clearance gate, mirrors bizSAFE
  const [payCleared, setPayCleared] = useState(true)
  const [payReason, setPayReason] = useState('')
  const [reason, setReason] = useState('')
  const [sendTo, setSendTo] = useState('')
  const [viewing, setViewing] = useState(null)   // { url, title } — report PDF shown in the page
  const [dirty, setDirty] = useState(false)      // unsaved edits (as bizSAFE "● Unsaved changes")
  const [jtab, setJtab] = useState(null)         // 'plan' | 'report' (as bizSAFE's workflow tabs)
  const [mode, setMode] = useState('view')       // as bizSAFE: opens read-only; "Edit" to change the plan
  const [history, setHistory] = useState({ trail: [], emails: [] })   // Records & history (office only)
  const [sessionPhoto, setSessionPhoto] = useState(null)              // Session evidence (internal only)
  const [sessionPhotoUrl, setSessionPhotoUrl] = useState(null)
  const firstLoad = useRef(true)

  useEffect(() => { load() }, [id])

  // As bizSAFE: a plan not sent yet opens ready to edit (once per job); a sent plan opens read-only.
  const autoEditFor = useRef(null)
  useEffect(() => {
    if (!job || autoEditFor.current === job.id) return
    autoEditFor.current = job.id
    if (!job.ia_plan_token && !job.audit_plan_sent_at && job.status !== 'Cancelled') setMode('edit')
  }, [job])

  async function load() {
    const { data: j } = await supabase.from('jobs').select('*, clients(*)').eq('id', id).maybeSingle()
    if (!j) { setLoading(false); return }
    const [{ data: cys }, { data: aus }, { data: reps }, { data: trail }, { data: mails }, { data: photos }, { data: cls }] = await Promise.all([
      supabase.from('client_ia_cycles').select('*').eq('client_id', j.client_id).order('created_at'),
      // All auditors (not only active): an assigned auditor whose record was later made inactive must stay on the job
      // when the plan is saved again. Only active, approved auditors are offered for new picks (eligible below).
      supabase.from('auditors').select('*').order('name'),
      supabase.from('ia_reports').select('id, kind, version, status, error, created_at, created_by').eq('job_id', id).order('created_at', { ascending: false }),
      // Records & history: the server-written report steps (ia-actions), plus — for jobs from before those were
      // recorded — the client report emails in the email log.
      supabase.from('audit_trail').select('action, performer_name, created_at, details').eq('entity_id', id).like('action', 'IA %').order('created_at'),
      supabase.from('outbound_emails').select('recipient, subject, created_at').eq('category', 'ia_report').eq('meta->>job_id', id).order('created_at'),
      supabase.from('audit_evidence_files').select('id, storage_path, file_name, created_at').eq('job_id', id).eq('evidence_type', 'session_photo')
        .is('archived_at', null).order('created_at', { ascending: false }).limit(1),
      supabase.from('ia_checklists').select('id, standards, active, sort_order, created_at'),
    ])
    const cy = (cys || []).find(c => c.id === j.ia_cycle_id) || null
    setJob(j); setCycles(cys || []); setCycle(cy); setAuditors(aus || []); setReports(reps || []); setChecklists(cls || null)
    setHistory({ trail: trail || [], emails: mails || [] })
    const photo = photos?.[0] || null
    setSessionPhoto(photo)
    if (photo?.storage_path) {
      const { data: u } = await supabase.storage.from('audit-evidence').createSignedUrl(photo.storage_path, 1800)
      setSessionPhotoUrl(u?.signedUrl || null)
    } else setSessionPhotoUrl(null)
    const std = cy?.standards || []
    let plan = j.ia_plan && j.ia_plan.items ? { ...j.ia_plan } : null
    if (!plan && j.ia_cycle_id) {
      // Recurring client: start from the plan sent for this client's last audit (same standards) — its items, site
      // inspection, days and start times. Dates and the audit location are NOT carried (always set fresh).
      const { data: prev } = await supabase.from('jobs').select('ia_plan, audit_plan_sent_at').eq('ia_cycle_id', j.ia_cycle_id)
        .neq('id', id).not('ia_plan_token', 'is', null).order('audit_plan_sent_at', { ascending: false }).limit(1)
      const p = prev?.[0]?.ia_plan
      if (p?.items && sameStandards(p.standards, std))
        // Site inspection is NOT carried over — only a person ticks it, for this audit (owner, 4 Nov 2026).
        plan = { items: withSiteWalk(p.items, false), standards: p.standards, siteWalk: false, edited: !!p.edited, version: p.version || 0, location: '',
          days: planDays(p).map(d => ({ ...d, date: '' })), carriedFrom: prev[0].audit_plan_sent_at }
    }
    if (!plan) plan = newPlan(std, { mandays: j.mandays })
    // The plan items are GENERATED from the standards, so items built for a different list are wrong. The
    // usual cause: the plan was created before the job was linked to its cycle, when the standards list was
    // still empty — which yields a plan with no items at all. Rebuild it here, but never once the office has
    // edited the items by hand (edited) or the plan has been sent (ia_plan_token) — those stay exactly as they are.
    const itemsForOtherStandards = !j.ia_plan_token && !plan.edited && !sameStandards(plan.standards, std)
    plan = { ...plan, standards: std, days: planDays(plan) }
    if (itemsForOtherStandards) plan.items = defaultPlanItems(std)
    // Site inspection is always the last row — moves it in older plans not yet sent (a sent plan stays as sent).
    if (!j.ia_plan_token && plan.siteWalk) plan.items = withSiteWalk(plan.items, true, std)
    firstLoad.current = true
    setDirty(false)
    setF({ audit_date: j.audit_date || '', auditor_id: j.auditor_id || '', auditor2_id: j.auditor2_id || '',
      report_reference_no: j.report_reference_no || '', mandays: j.mandays != null ? String(j.mandays) : '', plan })
    setSendTo(j.ia_report?.pending?.to || j.ia_report?.last_to || iaContactOf(j.clients).email || '')
    setLoading(false)
  }

  // ── Save draft (as bizSAFE): edits stay on screen until "Save draft"; Send needs a saved plan ─────────────────
  useEffect(() => {
    if (!f) return
    if (firstLoad.current) { firstLoad.current = false; return }
    setDirty(true)
  }, [f])

  // Unsaved changes: warn before refresh/close and before leaving through an in-app link (as bizSAFE).
  useEffect(() => {
    if (!dirty) return
    const LEAVE_MSG = 'You have unsaved changes. Are you sure you want to leave without saving?'
    const onBeforeUnload = e => { e.preventDefault(); e.returnValue = ''; return '' }
    const onClickCapture = e => {
      const a = e.target.closest && e.target.closest('a[href]')
      if (!a) return
      const href = a.getAttribute('href')
      if (!href || a.target === '_blank' || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return
      if (href === window.location.pathname) return
      if (!window.confirm(LEAVE_MSG)) { e.preventDefault(); e.stopPropagation() }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClickCapture, true)
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); document.removeEventListener('click', onClickCapture, true) }
  }, [dirty])

  async function saveDraft() {
    const a1 = auditors.find(a => a.id === f.auditor_id), a2 = auditors.find(a => a.id === f.auditor2_id)
    const md = String(f.mandays).trim()
    if (md && !(Number(md) > 0)) return toast('Man-days must be a number', 'warning', { detail: 'For example 0.5, 1 or 2' })
    // The same person can't be both auditors — the report could then never be signed by "the second auditor".
    if (f.auditor_id && f.auditor_id === f.auditor2_id) return toast('Pick two different auditors', 'warning', { detail: 'Or set the second auditor to None' })
    const days = planDays(f.plan, f.audit_date)
    const patch = {
      audit_date: f.audit_date || null, report_reference_no: f.report_reference_no.trim() || null,
      auditor_id: a1?.id || null, auditor_name: a1?.name || null,
      auditor2_id: a2?.id || null, auditor2_name: a2?.name || null, mandays: md ? Number(md) : null,
      ia_plan: { ...f.plan, days }, audit_site_address: String(f.plan.location || '').trim() || null,
    }
    setSaveState('Saving…')
    const { error } = await supabase.from('jobs').update(patch).eq('id', id)
    if (error) { setSaveState(''); return failed("Couldn't save", error.message) }
    setJob(j => ({ ...j, ...patch }))
    setDirty(false); setSaveState('')
    toast('Saved', 'success')
    return true
  }

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>
  if (!job) return <div>Job not found.</div>

  const client = job.clients || {}
  const std = cycle?.standards || []
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  const setPlan = patch => setF(x => ({ ...x, plan: { ...x.plan, ...patch } }))
  const status = iaJobStatus(job)
  const eligible = auditors.filter(a => a.active !== false && approved(a))
  const notOk = a => a.active === false ? 'inactive' : !approved(a) ? 'not approved' : null
  const a1 = auditors.find(a => a.id === f.auditor_id), a2 = auditors.find(a => a.id === f.auditor2_id)
  const paid = !!job.invoice_cleared_confirmed
  const stage = job.ia_stage
  const sent = !!job.ia_report_sent_at
  const planUrl = job.ia_plan_token ? `${window.location.origin}/ia-plan/${job.ia_plan_token}` : null
  const days = planDays(f.plan, f.audit_date)
  const setDays = list => setPlan({ days: list.map((d, i) => (i === 0 ? { ...d, date: f.audit_date } : d)) })
  const setDay = (i, patch) => { if (i === 0 && 'date' in patch) set('audit_date', patch.date); setDays(days.map((d, j) => (j === i ? { ...d, ...patch } : d))) }
  // "15 Oct 2026, 9.00am – 5.30pm" per day (client email, auditor email, job record).
  const scheduleLines = list => list.map(d => { const t = dayTimes(d); return `${d.date ? emailDate(d.date) : 'date to be confirmed'}, ${t.from} – ${t.to}${d.closeout ? ' (close-out)' : ''}` })
  const hasCloseout = days.some(d => d.closeout)
  // Set up in AuditPM = ONE checklist for exactly these standards (a single-standard checklist, or the integrated one,
  // e.g. 9001 + 14001 + 45001 = QEHS) AND plan rows for every standard. Otherwise the plan can't be sent (owner, 5 Nov
  // 2026) — an integrated audit must never fall back to separate checklists. Unknown until the library has loaded.
  const pick = checklists && std.length ? pickChecklists(std, checklists) : null
  const setupMissing = !!pick && (pick.lists.length !== 1 || pick.missing.length > 0 || unplannedStandards(std).length > 0)
  const setupMissingText = `This is ${std.length > 1 ? 'an integrated' : 'an'} ${standardsLabel(std)} audit. Please contact Admin to add the checklist and audit plan before sending the audit plan to the client.`
  // New audit days go before the close-out day (which always stays last).
  const addAuditDay = () => setDays(hasCloseout ? [...days.slice(0, -1), newDay('full'), days[days.length - 1]] : [...days, newDay('full')])
  // A gentle check (never blocks): a full day starting in the afternoon, or any day ending late.
  const timeWarning = d => {
    const end = dayEnd(d)
    if (end > '19:00') return `Check the times – this day ends at ${dayTimes(d).to}`
    if (d.part === 'full' && String(d.start || '') >= '12:00') return `Check the times – a full day starting at ${dayTimes(d).from}`
    return null
  }
  const tab = jtab || (job.ia_plan_token ? 'report' : 'plan')
  const outdated = (f.plan.version || 0) < PLAN_VERSION

  async function act(label, body, okTitle, { type = 'success', detail } = {}) {
    setBusy(label)
    try { await iaAction({ jobId: id, ...body }); if (okTitle) toast(okTitle, type, { detail }); await load() }
    catch (e) { failed("Couldn't complete that action", e.message) }
    setBusy('')
  }

  async function saveStandards(list) {
    if (!cycle) return
    const { error } = await supabase.from('client_ia_cycles').update({ standards: list, updated_at: new Date().toISOString() }).eq('id', cycle.id)
    if (error) return failed("Couldn't save", error.message)
    setCycle(c => ({ ...c, standards: list }))
    // Plan follows the standards unless its items were changed by hand.
    if (!f.plan.edited) setPlan({ standards: list, items: withSiteWalk(defaultPlanItems(list), f.plan.siteWalk, list) })
  }

  async function linkCycle(cycleId) {
    if (!cycleId || cycleId === job.ia_cycle_id) return
    if (job.ia_cycle_id && !window.confirm('Link this job to a different internal audit record? Its reminders and last internal audit date will follow this job.')) return
    const { error } = await supabase.from('jobs').update({ ia_cycle_id: cycleId }).eq('id', id)
    if (error) return failed("Couldn't link it", error.message)
    load()
  }

  // Cancel contract (before any report is sent) — same flow as bizSAFE (lib/contractCancel.js): the job and its signed
  // quotation are cancelled together, with the reason. Frees the client's reminders again (form mark cleared).
  // Multi-year contracts: cancelling the contract's first job (the signed contract quotation) ends the contract on
  // the client's internal audit record; cancelling a later "(Year n)" job gives that audit back to the contract.
  // Reopen reverses both.
  const contractRef = cycle?.contract_quote_ref || null
  const isYearJob = !!(contractRef && job.quote_ref && job.quote_ref.startsWith(contractRef + ' ('))
  async function updateCycle(patch) {
    const { error } = await supabase.from('client_ia_cycles').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', cycle.id)
    if (error) throw new Error('The job was updated, but the client’s contract record could not be: ' + error.message + '\nPlease correct it on the Clients page (Internal Audit → Edit).')
  }
  async function cancelJob() {
    const why = window.prompt('Cancel this contract? The job and its signed quotation will be cancelled.\n\nPlease give a reason:')
    if (why === null) return
    if (!why.trim()) return toast('Enter a reason', 'warning')
    try {
      const r = await cancelContract({ job, reason: why.trim(), by: profile?.name })
      await logAction?.('Contract cancelled', 'job', id, client.name, { reason: why.trim(), quotations: r.quoteRefs,
        contract: cycle && contractRef === job.quote_ref ? { until: cycle.contract_until, audits_left: cycle.contract_audits_left } : undefined })
      if (cycle) {
        // The client's form for this audit is used up → reminders may run again (an open job pauses them by itself).
        const patch = { form_submitted_at: null }
        if (contractRef && contractRef === job.quote_ref) Object.assign(patch, { contract_quote_ref: null, contract_audits_left: null, contract_until: null })
        else if (isYearJob) patch.contract_audits_left = (cycle.contract_audits_left || 0) + 1
        await updateCycle(patch)
      }
      toast('Contract cancelled', 'success', { detail: 'Let the auditor, Accounts and the client know' })
    } catch (e) { failed("Couldn't cancel", e.message) }
    load()
  }
  async function reopenJob() {
    if (!window.confirm('Reopen this job? Its quotation goes back to Signed.')) return
    try {
      const r = await reopenContract({ job, status: job.ia_plan_token ? 'Scheduled' : 'Confirmed', by: profile?.name })
      await logAction?.('Contract reopened', 'job', id, client.name, { quotations: r.quoteRefs })
      if (cycle && isYearJob) await updateCycle({ contract_audits_left: Math.max(0, (cycle.contract_audits_left || 0) - 1) })
      else if (cycle && job.quote_ref) {
        // A multi-year contract quotation brought back → the contract again, as when it was signed (year 1 = this job).
        const { data: q } = await supabase.from('quotations').select('quote_ref, ia_contract_years, signed_date').eq('quote_ref', job.quote_ref).maybeSingle()
        const years = q?.ia_contract_years || 1
        if (years > 1) {
          const until = new Date(q.signed_date || new Date()); until.setFullYear(until.getFullYear() + years); until.setDate(until.getDate() - 1)
          await updateCycle({ contract_quote_ref: q.quote_ref, contract_audits_left: years - 1, contract_until: until.toISOString().slice(0, 10) })
        }
      }
      toast('Job reopened', 'success')
    } catch (e) { failed("Couldn't reopen", e.message) }
    load()
  }

  // Fields Send needs — shared by "Send audit plan" and the "Still needed" line.
  function planMissingList() {
    const miss = []
    days.forEach((d, i) => { if (!d.date && !d.closeout) miss.push(`Day ${i + 1} date`) })   // a close-out date may be "to be confirmed"
    if (!a1) miss.push('Auditor')
    if (!String(f.plan.location || '').trim()) miss.push('Audit site address')
    if (!std.length) miss.push('Standard(s)')
    return miss
  }

  async function sendPlan() {
    if (dirty) return toast('Save the draft first', 'warning', { detail: 'Then send the audit plan' })
    // An integrated audit must use ITS OWN checklist and plan, never separate ones (owner, 5 Nov 2026).
    if (setupMissing) return toast('Checklist and audit plan not set up yet', 'warning', { detail: setupMissingText })
    const miss = planMissingList()
    if (miss.length) return toast('Fill in the missing fields', 'warning', { detail: '• ' + miss.join('\n• ') })
    for (let i = 1; i < days.length; i++) {
      if (days[i].closeout && !days[i].date) continue
      if (days[i].date <= days[i - 1].date) return toast('Check the audit dates', 'warning', { detail: `Day ${i + 1} must be after Day ${i}` })
    }
    for (const a of [a1, a2].filter(Boolean)) if (!approved(a)) return toast(`${a.name} is not an approved auditor`, 'warning', { detail: 'Or their approval has expired' })
    setBusy('plan')
    const token = job.ia_plan_token || newToken()
    const start = String(days[0].start || '09:00').replace(':', '')
    const lines = scheduleLines(days)
    const timeText = days.length === 1 ? `${dayTimes(days[0]).from} – ${dayTimes(days[0]).to}` : lines.join('; ')
    const nowIso = new Date().toISOString()
    const patch = {
      // The client's contact person (name · number) goes into the sent plan.
      ia_plan_token: token, ia_plan: { ...f.plan, days, standards: std, contact: { name: iaContactOf(client).name || '', phone: iaContactOf(client).phone || '' } },
      start_time: start, audit_time_display: timeText,
      audit_site_address: f.plan.location.trim(), audit_plan_sent_at: nowIso, plan_sent_date: today(),
      ...(['Completed', 'Cancelled'].includes(job.status) ? {} : { status: 'Scheduled' }),
    }
    const { error } = await supabase.from('jobs').update(patch).eq('id', id)
    if (error) { setBusy(''); return failed("Couldn't send the audit plan", error.message) }
    await logAction?.('Internal audit plan sent', 'job', id, client.name, { audit_date: f.audit_date })
    const url = `${window.location.origin}/ia-plan/${token}`
    const names = [a1?.name, a2?.name].filter(Boolean).join(', ')
    const mail = planEmail({ greet: iaContactOf(client).name || client.name, when: lines, location: f.plan.location.trim(), auditors: names, planUrl: url })
    let copied = false
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': new Blob([mail.html], { type: 'text/html' }), 'text/plain': new Blob([mail.text], { type: 'text/plain' }) })])
      copied = true
    } catch { try { await navigator.clipboard.writeText(mail.text); copied = true } catch {} }
    // Auditor assignment emails (no COI).
    const fails = []
    for (const a of [a1, a2].filter(Boolean)) {
      if (!a.email) { fails.push(a.name); continue }
      try {
        const { error: e } = await supabase.functions.invoke('send-internal-email', { body: {
          to: a.email, subject: `Internal Audit assignment — ${client.name} — ${emailDate(f.audit_date)}`,
          html: emailHtml({ title: 'Internal Audit assignment', greeting: `Dear ${a.name},`,
            lines: ['You have been assigned to the internal audit below.', { html: `Audit plan: <a href="${url}" style="color:#006363;">${url}</a>` }],
            details: [['Company', client.name || '—'], ['Standard(s)', standardsLabel(std)], ['Date & time', lines.join('; ')],
              ['Location', f.plan.location.trim()], ['Audit team', names]],
            button: { label: 'Open the audit →', url: `${window.location.origin}/jobs/${id}/ia` } }),
        } })
        if (e) fails.push(a.name)
      } catch { fails.push(a.name) }
    }
    setBusy('')
    const notes = [copied ? 'Client email copied — paste it into the client’s email (Ctrl+V)' : 'Couldn’t copy the client email — use “Copy client email again”']
    if (fails.length) notes.push(`The email to ${fails.join(', ')} didn’t send — let them know`)
    toast('Audit plan sent', copied && !fails.length ? 'success' : 'warning', { detail: notes.join('\n') })
    load()
  }

  async function copyAgain() {
    const names = [a1?.name, a2?.name].filter(Boolean).join(', ')
    const mail = planEmail({ greet: iaContactOf(client).name || client.name, when: scheduleLines(planDays(job.ia_plan, job.audit_date)),
      location: job.audit_site_address || '', auditors: names, planUrl })
    try { await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': new Blob([mail.html], { type: 'text/html' }), 'text/plain': new Blob([mail.text], { type: 'text/plain' }) })]); toast('Client email copied', 'success') }
    catch { try { await navigator.clipboard.writeText(mail.text); toast('Client email copied', 'success') } catch { toast("Couldn't copy", 'error') } }
  }

  // As bizSAFE "View report": the stored PDF in the browser's own viewer (save / print / download from there).
  async function openReport(r) {
    try { const d = await iaAction({ action: 'report-url', jobId: id, reportId: r.id }); setViewing({ url: d.url, title: `${KIND_LABEL[r.kind]} v${r.version}`, reportId: r.id }) } catch (e) { failed("Couldn't open the report", e.message) }
  }
  // Saving from the browser's own PDF toolbar names the file after the stored object. This button instead
  // asks the server for a download URL, which carries the report's proper name (e.g. 32013-Company-v1.pdf).
  async function downloadReport(reportId) {
    try {
      const d = await iaAction({ action: 'report-url', jobId: id, reportId, download: true })
      const a = document.createElement('a'); a.href = d.url; a.rel = 'noopener'
      document.body.appendChild(a); a.click(); a.remove()
    } catch (e) { failed("Couldn't download the report", e.message) }
  }
  async function viewUpload() {
    try { const d = await iaAction({ action: 'view-upload', jobId: id }); window.open(d.url, '_blank') } catch (e) { failed("Couldn't open the file", e.message) }
  }

  const stale = r => r.status === 'error' || (r.status === 'preparing' && Date.now() - new Date(r.created_at).getTime() > 5 * 60000)
  // A send normally takes seconds; after 10 minutes it has stopped, and the report may be returned to send again.
  const sendingStuck = stage === 'sending' && (!job.ia_report?.sending_since || Date.now() - new Date(job.ia_report.sending_since).getTime() > 10 * 60000)
  // Full day / Morning / Afternoon for one audit day — one joined button group per day.
  const partBtn = (i, d, k, ki) => {
    const on = d.part === k
    return <button key={k} type="button" onClick={() => setDay(i, { part: k, start: PARTS[k].start, end: '' })}
      style={{ padding: '7px 13px', fontSize: 13, borderRadius: ki === 0 ? '8px 0 0 8px' : ki === 2 ? '0 8px 8px 0' : 0, marginLeft: ki ? -1 : 0,
        border: `1px solid ${on ? 'var(--brand)' : 'var(--border)'}`, position: 'relative', zIndex: on ? 1 : 0, whiteSpace: 'nowrap',
        background: on ? 'var(--brand-light)' : 'var(--bg)', color: on ? 'var(--brand-dark)' : 'var(--text)', fontWeight: on ? 600 : 400, cursor: 'pointer' }}>{PARTS[k].label}</button>
  }

  // The client's latest external audit report, uploaded by the office (when the client did not upload it).
  async function uploadReport(file) {
    if (!file || !cycle) return
    if (!/\.pdf$/i.test(file.name)) return toast('Choose a PDF', 'warning')
    setBusy('upload')
    try {
      const d = await iaAction({ action: 'upload-url', jobId: id, filename: file.name, size: file.size })
      const { error: upErr } = await supabase.storage.from('ia-client-uploads').uploadToSignedUrl(d.path, d.uploadToken, file, { contentType: 'application/pdf' })
      if (upErr) throw new Error('Upload failed: ' + upErr.message)
      const patch = { upload_path: d.path, upload_name: file.name, uploaded_at: new Date().toISOString() }
      const { error } = await supabase.from('client_ia_cycles').update(patch).eq('id', cycle.id)
      if (error) throw new Error('Could not save: ' + error.message)
      setCycle(c => ({ ...c, ...patch }))
      toast('External audit report uploaded', 'success')
    } catch (e) { failed('Upload failed', e.message) }
    setBusy('')
  }

  // Page width 1200 (was 1000): the Audit days row with two auditors needs ~1150px — no sideways scroll on a normal screen.
  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ color: 'var(--brand)', cursor: 'pointer' }} onClick={() => navigate('/jobs')}>← Jobs</span></div>
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>{client.name}</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>Internal Audit · {standardsLabel(std) || 'standard(s) not set'}{job.quote_ref ? ` · ${job.quote_ref}` : ''}</div>
          <IAGroupLine cycleId={job.ia_cycle_id} style={{ marginTop: 2 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge badge-info">{status}</span>
          {mode === 'view'
            ? <>
                {job.status !== 'Cancelled' && <button className="primary sm" onClick={() => { setJtab('plan'); setMode('edit') }}>Edit</button>}
                {job.status !== 'Cancelled' && !sent && !stage && <button className="sm" onClick={cancelJob}>Cancel contract</button>}
              </>
            : <>
                <button className="sm" disabled={saveState === 'Saving…'} onClick={async () => { if (await saveDraft()) { setEditItems(false); setEditStd(false); setMode('view') } }} style={{ background: 'none', color: 'var(--text2)' }}>Save &amp; close</button>
                <button className="sm" onClick={async () => { await load(); setEditItems(false); setEditStd(false); setMode('view') }}>Cancel</button>
              </>}
        </div>
      </div>
      {job.status === 'Cancelled' && (
        <div style={{ background: 'var(--bg2)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          This job is cancelled.{lastCancelNote(job.notes) && <span style={{ color: 'var(--text2)' }}>{lastCancelNote(job.notes)}</span>}
          <button className="sm" onClick={reopenJob}>Reopen</button>
        </div>)}

      {/* ── Workflow tabs (as bizSAFE): planning, then the audit, payment and reports ── */}
      <div className="page-tabs">
        {[['plan', 'Plan & Prepare', !job.ia_plan_token && job.status !== 'Cancelled'],
          ['report', 'Audit & Report', stage === 'awaiting_payment' || stage === 'awaiting_closeout']].map(([v, l, dot]) => (
          <button key={v} onClick={() => setJtab(v)} className={tab === v ? 'page-tab active' : 'page-tab'}>
            {l}{dot && <span title="Action pending" className="page-tab-dot" />}
          </button>))}
      </div>

      {/* As bizSAFE: in view mode the .view-mode class shows inputs as plain text and hides the .edit-only buttons;
          the Audit & Report cards are .action-live (payment, return, resend links work without Edit). */}
      <fieldset className={mode === 'view' ? 'view-mode' : undefined} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
      {tab === 'plan' && <div className="plan-cards">
      {/* ── 1 · Client & quotation: read-only reference ───────────── */}
      <div className="card">
        <h2 className="card-title">Client &amp; quotation</h2>
        <div className="info-rows">
          <div className="info-row"><div className="info-label">Contact</div><div><IAContactLine clientId={client.id} onSaved={load} /></div></div>
          <div className="info-row"><div className="info-label">Certification body · certificate expiry</div><div>{cycle?.certification_body || '—'} · {cycle?.cert_expiry ? emailDate(cycle.cert_expiry) : '—'}</div></div>
          {cycle && (cycle.contract_audits_left > 0 || cycle.contract_quote_ref) && (
            <div className="info-row"><div className="info-label">Contract</div>
              <div>{cycle.contract_quote_ref || 'Multi-year'}{cycle.contract_until ? ` · until ${emailDate(cycle.contract_until)}` : ''} · {cycle.contract_audits_left ?? 0} audit{cycle.contract_audits_left === 1 ? '' : 's'} left after this</div></div>)}
        </div>
      </div>

      {/* ── 2 · Audit plan: everything the office fills in ────────── */}
      <div className="card plan-form">
        <h2 className="card-title">Audit plan</h2>
        {!cycle && (
          <div style={{ background: '#fff4e0', padding: '10px 12px', borderRadius: 8, fontSize: 14, marginBottom: 10 }}>
            This job is not linked to an internal audit cycle.{' '}
            <select className="edit-only" defaultValue="" onChange={e => linkCycle(e.target.value)} style={{ width: 'auto', marginLeft: 6 }}>
              <option value="">Link to…</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{standardsLabel(c.standards) || 'Cycle (no standard)'}</option>)}
            </select>
          </div>
        )}
        {cycle && cycles.length > 1 && (
          <div className="info-row">
            <div className="info-label">Internal audit record (this client has {cycles.length})</div>
            <select value={cycle.id} onChange={e => linkCycle(e.target.value)} style={{ width: 'auto' }}>
              {cycles.map(c => <option key={c.id} value={c.id}>{standardsLabel(c.standards) || 'No standard set'}{c.last_ia_date ? ` · last IA ${emailDate(c.last_ia_date)}` : ''}{c.active ? '' : ' · reminders stopped'}</option>)}
            </select>
          </div>)}
        <div className="info-row">
          <div className="info-label">Standard(s)</div>
          {!editStd
            ? <div>{standardsLabel(std) || '—'} {cycle && <button className="sm edit-only" style={{ marginLeft: 6 }} onClick={() => { setStdOpts(iaStandardOptions(std)); setEditStd(true) }}>Change</button>}</div>
            : <div>{stdOpts.map(s => (
                <label key={s} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ width: 'auto' }} checked={std.includes(s)}
                    onChange={() => saveStandards(std.includes(s) ? std.filter(x => x !== s) : stdOpts.filter(x => x === s || std.includes(x)))} /> {s}
                </label>))}
                <button className="sm" onClick={() => setEditStd(false)}>Done</button></div>}
        </div>
        {/* Audit site: NEVER filled in for the office (as bizSAFE) — typed, or copied from the company (HQ) address. */}
        <div className="field">
          <label>Audit site address *
            <span className="edit-only" style={{ color: 'var(--brand)', cursor: 'pointer', fontWeight: 400 }}
              onClick={() => client.address ? setPlan({ location: oneLine(client.address) })
                : toast('No HQ address for this client', 'warning', { detail: 'Add it on the Clients page, or type the site here' })}>
              Copy HQ address{client.address && f.plan.location === oneLine(client.address) ? ' ✓' : ''}</span></label>
          {mode === 'view'
            ? <div className="plan-text">{oneLine(f.plan.location) || '—'}</div>
            : <input value={oneLine(f.plan.location)} onChange={e => setPlan({ location: oneLine(e.target.value) })} placeholder={mode === 'view' ? '—' : 'Enter audit site or copy from HQ'} />}</div>
        <div className="field"><label>Auditor</label>
          <select value={f.auditor_id} onChange={e => set('auditor_id', e.target.value)}>
            <option value="">Select…</option>
            {eligible.filter(a => a.id !== f.auditor2_id).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            {a1 && notOk(a1) && <option value={a1.id}>{a1.name} ({notOk(a1)})</option>}
          </select>
          {a1 && notOk(a1) && <div style={{ fontSize: 14, color: '#854F0B', marginTop: 6 }}>⚠ {a1.name} {notOk(a1) === 'inactive' ? 'is no longer an active auditor' : 'is not an approved auditor — or their approval has expired'}</div>}</div>
        <div className="field"><label>Second auditor (if any)</label>
          <select value={f.auditor2_id} onChange={e => set('auditor2_id', e.target.value)}>
            <option value="">None</option>
            {eligible.filter(a => a.id !== f.auditor_id).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            {a2 && notOk(a2) && <option value={a2.id}>{a2.name} ({notOk(a2)})</option>}
          </select>
          {a2 && notOk(a2) && <div style={{ fontSize: 14, color: '#854F0B', marginTop: 6 }}>⚠ {a2.name} {notOk(a2) === 'inactive' ? 'is no longer an active auditor' : 'is not an approved auditor — or their approval has expired'}</div>}</div>
        <div className="field"><label>Man-days (from the quote)</label>
          <input className="plan-w-sm" value={f.mandays} onChange={e => set('mandays', e.target.value)} inputMode="decimal" placeholder="e.g. 0.5, 1, 2" /></div>
        {/* Audit days: one line per day (most audits: just Day 1). Man-days do not decide the days — the office does. */}
        <div className="plan-days-ia">
          {/* One tidy row per day under column headings: Date · Length · Start · Ends · (Auditor, with 2 auditors). */}
          {(() => {
            const cols = `170px 150px auto 112px 112px${a2 ? ' 190px' : ''} 32px`
            const head = { fontSize: 13, color: 'var(--text2)' }
            return (
              <div style={{ display: 'grid', gridTemplateColumns: cols, gap: '8px 16px', alignItems: 'center', justifyContent: 'start', overflowX: 'auto' }}>
                <span className="plan-days-title">Audit days</span><span style={head}>Date</span><span style={head}>Length</span>
                <span style={head}>Start</span><span style={head}>Ends</span>{a2 && <span style={head}>Auditor(s)</span>}<span />
                {days.map((d, i) => (
                  <React.Fragment key={i}>
                    <b style={{ fontSize: 13 }}>{d.closeout ? 'Close-out' : `Day ${i + 1}`}</b>
                    <input type="date" value={d.date || ''} onChange={e => setDay(i, { date: e.target.value })} />
                    <div className="edit-lock" style={{ display: 'inline-flex' }}>{['full', 'am', 'pm'].map((k, ki) => partBtn(i, d, k, ki))}</div>
                    <input type="time" value={d.start || ''} onChange={e => setDay(i, { start: e.target.value, end: '' })} />
                    {/* Shows the usual end for this length; change it when an audit ends later (or earlier). */}
                    <input type="time" value={dayEnd(d)} onChange={e => setDay(i, { end: e.target.value })} title="Change if this audit ends at a different time" />
                    {a2 && <select value={d.who || 'both'} onChange={e => setDay(i, { who: e.target.value })}>
                      <option value="both">Both auditors</option><option value="a1">{a1?.name || 'Auditor'} only</option><option value="a2">{a2.name} only</option></select>}
                    {/* Own grid cell, so hiding the button in view mode does not shift the next row. */}
                    <span>{i > 0 && <button type="button" className="sm edit-only" title="Remove this day" style={{ width: 32, padding: 0, justifyContent: 'center' }} onClick={() => setDays(days.filter((_, j) => j !== i))}>×</button>}</span>
                    {timeWarning(d) && <span style={{ gridColumn: '2 / -1', fontSize: 13, color: '#854F0B', marginTop: -4 }}>⚠ {timeWarning(d)}</span>}
                  </React.Fragment>))}
              </div>)
          })()}
          <div className="edit-only plan-indent" style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="sm" onClick={addAuditDay}>+ Add another audit day</button>
            {!hasCloseout && <button type="button" className="sm" onClick={() => setDays([...days, newCloseoutDay()])}>+ Add close-out day (TA2)</button>}
          </div>
          {/* Only some clients get a site inspection (e.g. a plant). Never pre-ticked: only a person ticks it. The
              clickable area is the box and its words only (a full-width row was ticked by stray clicks, 4 Nov 2026). */}
          {mode === 'view'
            ? <div className="plan-indent" style={{ fontSize: 14, marginTop: 12, color: 'var(--text2)' }}>Site inspection: {f.plan.siteWalk ? 'Yes' : 'No'}</div>
            : <div className="plan-indent" style={{ marginTop: 12 }}>
                <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                  <input type="checkbox" style={{ width: 'auto' }} checked={!!f.plan.siteWalk}
                    onChange={e => setPlan({ siteWalk: e.target.checked, items: withSiteWalk(f.plan.items, e.target.checked, std) })} />
                  Include a site inspection
                </label>
              </div>}
        </div>
        <div className="info-row"><div className="info-label">Client’s latest external audit report</div>
          <div>{cycle?.upload_path ? <button className="sm" onClick={viewUpload}>View</button>
            : cycle && mode === 'view' ? <div>—</div>
            : cycle && <label className="sm" style={{ display: 'inline-flex', alignItems: 'center', height: 32, cursor: busy ? 'default' : 'pointer', padding: '0 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
                {busy === 'upload' ? 'Uploading… please wait' : 'Upload PDF'}
                <input type="file" accept="application/pdf,.pdf" disabled={!!busy} style={{ display: 'none' }} onChange={e => { uploadReport(e.target.files[0]); e.target.value = '' }} /></label>}</div></div>
        {/* As bizSAFE: the report uses the quote number. A Report Ref No. is only needed for a job that has no
            AuditPM quote (signed outside the system). */}
        {!job.quote_ref && <div className="field"><label>Report Ref No. (no AuditPM quote for this job)</label>
          <input value={f.report_reference_no} onChange={e => set('report_reference_no', e.target.value)} placeholder="e.g. REPORT/32013-26/LL" /></div>}
      </div>

      {/* ── 3 · Audit schedule: built from the audit plan ─────────── */}
      <div className="card">
        <h2 className="card-title">Audit schedule</h2>
        {f.plan.carriedFrom && <div style={{ background: 'var(--bg2)', padding: '8px 12px', borderRadius: 8, fontSize: 14, marginBottom: 10 }}>
          Same plan as last audit (sent {emailDate(String(f.plan.carriedFrom).slice(0, 10))}). Change anything below if needed.</div>}
        {outdated && <div style={{ background: '#fff4e0', padding: '8px 12px', borderRadius: 8, fontSize: 14, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <b>A newer CCIS audit plan is available.</b>
          <button className="sm primary edit-only" onClick={() => setPlan({ items: withSiteWalk(defaultPlanItems(std), f.plan.siteWalk, std), version: PLAN_VERSION, edited: false })}>Update plan</button></div>}
        {setupMissing && <div style={{ background: '#fff4e0', padding: '8px 12px', borderRadius: 8, fontSize: 14, marginBottom: 10 }}>
          <b>Checklist and audit plan not set up yet.</b> {setupMissingText}</div>}
        {/* Folded to the first rows (details + start of the timetable) so the page stays short; "Show full plan" opens it. */}
        <div style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: 8, padding: 14, overflowX: 'auto', background: '#fff',
          ...(planOpen ? {} : { maxHeight: 420, overflowY: 'hidden' }) }}>
          <IAPlanDocument company={client.name} auditDate={f.audit_date} auditors={[a1?.name, a2?.name]} standards={std}
            plan={{ ...f.plan, contact: undefined }} contact={{ name: iaContactOf(client).name, phone: iaContactOf(client).phone }} />
          {!planOpen && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 90, background: 'linear-gradient(rgba(255,255,255,0), #fff 85%)', pointerEvents: 'none' }} />}
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          <button className="sm" onClick={() => setPlanOpen(v => !v)}>{planOpen ? 'Fold plan ▴' : 'Show full plan ▾'}</button>
          <button className="sm edit-only" onClick={() => setEditItems(v => !v)}>{editItems ? 'Hide plan items' : 'Edit plan items'}</button>
        </div>
        {editItems && mode === 'edit' && <PlanItemsEditor plan={f.plan} standards={std}
          onChange={items => setPlan({ items, edited: true })}
          onReset={() => { if (window.confirm('Put the plan items back to the standard list?')) setPlan({ items: withSiteWalk(defaultPlanItems(std), f.plan.siteWalk, std), version: PLAN_VERSION, edited: false }) }} />}
      </div>

      {/* ── 4 · Send & replies ─────────────────────────────────────── */}
      {(mode === 'edit' || (!dirty && job.audit_plan_sent_at)) && (
      <div className="card">
        <h2 className="card-title">Send &amp; replies</h2>
        {/* Why Send would stop — the same checks "Send audit plan" makes, in the field names shown above. */}
        {mode === 'edit' && planMissingList().length > 0 && (() => {
          const miss = planMissingList()
          return <div className="plan-still">Still needed: {miss.slice(0, 3).join(', ')}{miss.length > 3 ? `, +${miss.length - 3} more` : ''}</div>
        })()}
        {/* Action area — as bizSAFE: Save draft · Send audit plan (Send needs a saved plan). */}
        {mode === 'edit' && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={saveDraft} disabled={saveState === 'Saving…'} style={{ background: dirty ? 'var(--warn)' : undefined, color: dirty ? '#fff' : undefined, borderColor: dirty ? 'var(--warn)' : undefined }}>
            {saveState === 'Saving…' ? 'Saving…' : dirty ? '● Save draft' : 'Save draft'}
          </button>
          <button className="primary" disabled={busy === 'plan' || dirty} onClick={sendPlan}
            style={dirty ? { background: 'var(--bg2)', color: 'var(--text3)', borderColor: 'var(--border)', cursor: 'not-allowed' } : undefined}>
            {busy === 'plan' ? 'Sending…' : job.audit_plan_sent_at ? '📤 Send updated audit plan' : '📤 Send audit plan'}</button>
          {dirty && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>● Unsaved changes</span>}
        </div>}
        {!dirty && job.audit_plan_sent_at && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: mode === 'edit' ? 12 : 0 }}>
            <span style={{ fontSize: 14, color: 'var(--text2)' }}>Last sent {fmtDT(job.audit_plan_sent_at)}</span>
            <button className="sm" onClick={copyAgain}>Copy client email again</button>
            {planUrl && <button className="sm" onClick={() => window.open(planUrl, '_blank')}>Open plan page</button>}
          </div>)}
      </div>
      )}

      </div>}

      {tab === 'report' && <>
      {/* ── Payment ──────────────────────────────────────────────── */}
      <div className="card action-live" style={{ marginBottom: 14 }}>
        <h2 className="card-title">Payment</h2>
        {/* What is recorded about payment … */}
        {paid
          ? <div style={{ fontSize: 14 }}><span style={{ color: 'var(--brand)' }}>✓</span> Payment received{job.invoice_clearance_checked_by_name ? ` — recorded by ${job.invoice_clearance_checked_by_name}` : ''}{job.invoice_clearance_checked_at ? ` on ${fmtDT(job.invoice_clearance_checked_at)}` : ''}</div>
          : job.invoice_clearance_checked_at
          ? <div style={{ fontSize: 14, color: '#854F0B' }}>⚠ Released before payment{job.invoice_clearance_checked_by_name ? ` — by ${job.invoice_clearance_checked_by_name}` : ''}{job.invoice_clearance_checked_at ? ` on ${fmtDT(job.invoice_clearance_checked_at)}` : ''}
              {job.invoice_clearance_reason ? <div style={{ marginTop: 4, color: 'var(--text2)' }}>Reason: {job.invoice_clearance_reason}</div> : null}
              {/* The payment that arrives later can still be recorded. */}
              {stage !== 'awaiting_payment' && <div style={{ marginTop: 8 }}><button className="sm" disabled={!!busy} onClick={() => { if (window.confirm('Record that payment has now been received?')) act('paid', { action: 'mark-paid' }, 'Payment recorded') }}>Payment received</button></div>}</div>
          : stage !== 'awaiting_payment' && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: 'var(--text2)' }}>Payment not recorded yet</span>
                <button className="sm" disabled={!!busy} onClick={() => { if (window.confirm('Record that payment has been received?')) act('paid', { action: 'mark-paid' }, 'Payment recorded') }}>Payment received</button>
              </div>}
        {/* … and, while a report is waiting to go out, the Send button — ALWAYS, even if payment was already recorded
            (e.g. an earlier send failed), so the report can never be left without a way to send it. */}
        {stage === 'awaiting_payment' && <div style={{ marginTop: paid || job.invoice_clearance_checked_at ? 10 : 0 }}>
            <div className="field" style={{ maxWidth: 420 }}><label>Send the report to</label><input value={sendTo} onChange={e => setSendTo(e.target.value)} /></div>
            <button className="primary" disabled={!!busy} onClick={() => {
              if (!sendTo.trim()) return toast("Enter the client's email", 'warning')
              setPayCleared(true); setPayReason(''); setPayModal(true)
            }}>{busy === 'pay' ? 'Sending…' : 'Send Audit Report'}</button>
          </div>}
      </div>

      {/* ── Reports ──────────────────────────────────────────────── */}
      <div className="card action-live" style={{ marginBottom: 14 }}>
        <h2 className="card-title">Reports</h2>
        {stage === 'returned' && <div style={{ background: '#fdecec', padding: '8px 12px', borderRadius: 8, fontSize: 14, marginBottom: 10 }}>Returned to the auditor: {job.ia_return_reason}</div>}
        {!reports.length && !stage && <div style={{ fontSize: 14, color: 'var(--text3)' }}>Waiting for the auditor’s report</div>}
        {reports.length > 0 && (
          <table style={{ fontSize: 14, marginBottom: 10 }}><tbody>
            {reports.map(r => (
              <tr key={r.id}>
                <td>{KIND_LABEL[r.kind]} <span style={{ color: 'var(--text2)' }}>v{r.version}</span></td>
                <td style={{ color: 'var(--text2)' }}>{fmtDT(r.created_at)}{r.created_by ? ` · ${r.created_by}` : ''}</td>
                <td style={{ color: r.status === 'error' ? 'var(--danger)' : 'var(--text2)' }} title={r.error || ''}>{REPORT_STATUS[r.status] || r.status}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.status === 'ready' && <button className="sm" onClick={() => openReport(r)}>Open</button>}
                  {/* Never for a report the client already has — only one that is stuck / failed, or not sent yet. */}
                  {((r.status === 'ready' && !sent) || stale(r)) && <button className="sm" disabled={!!busy} onClick={() => act('rr', { action: 'rerender', reportIds: [r.id] }, 'Preparing again', { type: 'info', detail: 'Check back in a minute' })}>Prepare again</button>}
                </td>
              </tr>))}
          </tbody></table>)}
        {/* Auditors only: the office sees the same in Records & history (owner, 4 Nov 2026). */}
        {sent && profile?.role === 'Auditor' && <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 10 }}>Sent to {job.ia_report?.last_to || iaContactOf(client).email} on {fmtDT(job.ia_report_sent_at)}{job.ia_closeout_sent_at ? ` · Close-out report sent ${fmtDT(job.ia_closeout_sent_at)}` : ''}</div>}
        {stage === 'awaiting_closeout' && (
          <div style={{ background: '#fff4e0', padding: '8px 12px', borderRadius: 8, fontSize: 14, marginBottom: 10 }}>
            {/* From the SAVED plan — the same plan the reminder schedule reads (no reminders when there is a TA2 day). */}
            {(() => {
              const ta2 = job.ia_plan ? planDays(job.ia_plan).find(d => d.closeout) : null
              return ta2
                ? `Waiting for close-out on site (TA2) — ${ta2.date ? emailDate(ta2.date) : 'date to be confirmed'}`
                : 'Waiting for the client’s close-out evidence. Reminders go out 30 and 60 days after the report was sent.'
            })()}
          </div>)}
        {stage === 'sending' && <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 10 }}>
          {sendingStuck ? 'Sending seems to have stopped. You can return the report to the auditor to send it again.' : 'Sending the report to the client — please reload in a few minutes.'}</div>}
        {(['awaiting_payment', 'done', 'awaiting_closeout'].includes(stage) || (stage === 'sending' && sendingStuck)) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {!returning
              ? <button className="sm" onClick={() => setReturning(true)}>Return to auditor</button>
              : <div style={{ flex: '1 1 100%' }}>
                  <div className="field"><label>Why is it being returned?</label><textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} /></div>
                  <button className="sm primary" disabled={!!busy || !reason.trim()} onClick={async () => { await act('ret', { action: 'return', reason: reason.trim() }, 'Returned to the auditor'); setReturning(false); setReason('') }}>Return</button>
                  <button className="sm" style={{ marginLeft: 6 }} onClick={() => setReturning(false)}>Cancel</button>
                </div>}
            {sent && !returning && <button className="sm" disabled={!!busy} onClick={() => {
              const to = window.prompt('Send fresh 3-month download links to:', job.ia_report?.last_to || iaContactOf(client).email || '')
              if (to && to.trim()) act('links', { action: 'resend-links', to: to.trim() }, 'New links sent')
            }}>Resend Link to Client</button>}
            {/* Office only (the server allows only office staff); in the button row, not inside the note (owner, 5 Nov 2026). */}
            {stage === 'awaiting_closeout' && !returning && profile?.role !== 'Auditor' && <button className="sm" disabled={!!busy} onClick={() => { if (window.confirm('Confirm no close-out is needed?\n\nThe job will be closed and marked Completed. The auditor will not be able to send a Close-out Report after this.')) act('cnn', { action: 'closeout-not-needed' }, 'Job completed') }}>Close-out not needed</button>}
          </div>)}
      </div>

      {/* ══ RECORDS & HISTORY (office only) — who did what, when, as bizSAFE. Report steps are written by the server
            (ia-actions → audit_trail). Older jobs: rebuilt from the report dates, payment record and email log, for
            the period before the first server-written line (those lines can't say who sent the email). ══ */}
      {profile?.role !== 'Auditor' && (() => {
        const T = history.trail
        const cutoff = T.length ? new Date(T[0].created_at).getTime() : Infinity
        const before = d => d && new Date(d).getTime() < cutoff
        // details.by / details.at: a release copied into the log from the job when payment came in later.
        const by = r => (r.details?.by || r.performer_name) ? ` by ${r.details?.by || r.performer_name}` : ''
        const cc = r => r.details?.cc ? ' (cc Accounts)' : ''
        const quote = s => s ? ` — “${s}”` : ''
        const TRAIL_TEXT = {
          'IA report submitted': r => `Report submitted${by(r)}${r.details?.awaiting_payment ? ' — awaiting payment' : ''}`,
          'IA payment received': r => `Payment received — recorded${by(r)}`,
          'IA report released before payment': r => `Report released before payment${by(r)}${quote(r.details?.reason)}${r.details?.re_entered ? ' (re-entered)' : ''}`,
          'IA report emailed to client': r => `Report emailed to ${r.details?.to || 'the client'}${cc(r)}${by(r)}`,
          'IA report returned to auditor': r => `Returned to the auditor${by(r)}${quote(r.details?.reason)}`,
          'IA updated report emailed to client': r => `Updated report emailed to ${r.details?.to || 'the client'}${cc(r)}${by(r)}`,
          'IA close-out report emailed to client': r => `Close-out Report emailed to ${r.details?.to || 'the client'}${cc(r)}${by(r)}`,
          'IA close-out marked not needed': r => `Close-out marked not needed${by(r)}`,
          'IA close-out switched on': r => `Close-out Report switched on${by(r)} — not ticked when the report was submitted`,
          'IA report links resent': r => `Fresh download links sent to ${r.details?.to || 'the client'}${by(r)}`,
          'IA report signed by second auditor': r => `Report signed by the second auditor${r.performer_name ? ` (${r.performer_name})` : ''}`,
          'IA report unlocked for editing': r => `Report unlocked for editing${by(r)} — second auditor asked to sign again`,
        }
        const rows = []
        // Skip a rebuilt line when the log already has that step (no double line). The report row is saved a moment
        // BEFORE the server writes its "submitted" log line, so without this the first report showed twice.
        const logged = a => T.some(r => r.action === a)
        const firstReport = reports.length ? reports[reports.length - 1] : null   // reports are newest first
        if (firstReport && before(firstReport.created_at) && !logged('IA report submitted'))
          rows.push({ at: firstReport.created_at, text: `Report submitted${firstReport.created_by ? ` by ${firstReport.created_by}` : ''}` })
        // The job holds only the LATEST payment record; skip it when the log already has that step.
        if (before(job.invoice_clearance_checked_at)
            && !logged(job.invoice_cleared_confirmed ? 'IA payment received' : 'IA report released before payment'))
          rows.push({ at: job.invoice_clearance_checked_at, text: job.invoice_cleared_confirmed
            ? `Payment received — recorded${job.invoice_clearance_checked_by_name ? ` by ${job.invoice_clearance_checked_by_name}` : ''}`
            : `Report released before payment${job.invoice_clearance_checked_by_name ? ` by ${job.invoice_clearance_checked_by_name}` : ''}${quote(job.invoice_clearance_reason)}` })
        let firstSend = true
        history.emails.filter(m => before(m.created_at)).forEach(m => {
          const s = m.subject || ''
          const what = /close-out/i.test(s) ? 'Close-out Report emailed to'
            : /^updated/i.test(s) ? 'Updated report emailed to'
            : firstSend ? 'Report emailed to' : 'Fresh download links sent to'
          if (!/close-out|^updated/i.test(s)) firstSend = false
          rows.push({ at: m.created_at, text: `${what} ${m.recipient}` })
        })
        T.forEach(r => rows.push({ at: r.details?.at || r.created_at, text: (TRAIL_TEXT[r.action] || (x => x.action))(r) }))
        rows.sort((a, b) => new Date(a.at) - new Date(b.at))
        if (!rows.length) return null
        return (
          <div className="card" style={{ marginTop: 24 }}>
            <h2 className="card-title">Records &amp; history</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((r, i) => <div key={i} style={{ fontSize: 14 }}><span style={{ color: 'var(--brand)' }}>✓</span> {r.text} · {fmtDT(r.at)}</div>)}
            </div>
          </div>
        )
      })()}

      {/* ── Session evidence (INTERNAL ONLY — proof of site attendance; never in the report or shown to the client) ── */}
      {profile?.role !== 'Auditor' && (() => {
        const consent = job.ia_report?.photo_consent || null
        const noPhotoReason = String(job.ia_report?.no_photo_reason || '').trim()
        const submitted = reports.length > 0 || !!stage
        if (!sessionPhoto && !noPhotoReason && !submitted) return null   // audit not done yet — nothing to show
        return (
          <div className="card" style={{ marginTop: 16 }}>
            <h2 className="card-title">Session evidence <span className="card-title-note">(internal only)</span></h2>
            {sessionPhoto ? (<>
              <div style={{ fontSize: 14, marginBottom: 12 }}>
                Photo consent: <strong>{consent || 'not recorded'}</strong>
                <span style={{ color: 'var(--text2)' }}> (uploaded {fmtDT(sessionPhoto.created_at)})</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {sessionPhotoUrl && <button className="sm" onClick={() => window.open(sessionPhotoUrl, '_blank')}>View</button>}
                <button className="sm" onClick={async () => {
                  const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(sessionPhoto.storage_path, 120, { download: sessionPhoto.file_name || true })
                  if (data?.signedUrl) window.open(data.signedUrl, '_blank')
                }}>Download</button>
              </div>
            </>) : noPhotoReason ? (
              <div style={{ padding: '10px 14px', background: '#FFF3E0', border: '1px solid #E6A817', borderRadius: 8, fontSize: 14, color: '#6b4e00' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Session photo not uploaded</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{noPhotoReason}</div>
              </div>
            ) : (
              <div style={{ padding: '10px 14px', background: '#FDECEA', border: '1px solid #E24B4A', borderRadius: 8, fontSize: 14, color: '#791F1F' }}>
                ⚠ No session photo or reason recorded.
              </div>
            )}
          </div>
        )
      })()}

      </>}
      </fieldset>

      {payModal && (
        <div className="modal-overlay" onClick={() => !busy && setPayModal(false)}>
          <div className="modal action-live" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: 14 }}>Payment Clearance Check</h2></div>
            <p style={{ fontSize: 14, color: 'var(--text2)', margin: '4px 0 12px' }}>
              Before sending the audit report to {sendTo.trim() || 'the client'}, please confirm payment status.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 10 }}>
              <input type="checkbox" checked={payCleared} onChange={e => setPayCleared(e.target.checked)} />
              Payment has been received.
            </label>
            {!payCleared && (
              <div className="field" style={{ margin: 0 }}>
                <label>Reason for releasing the report before payment *</label>
                <textarea rows={3} value={payReason} onChange={e => setPayReason(e.target.value)}
                  placeholder="Example: Client urgent submission deadline, management approval obtained, etc." />
                {!payReason.trim() && <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>A reason is required when payment has not been received.</div>}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="sm" disabled={!!busy} onClick={() => setPayModal(false)}>Cancel</button>
              <button className="primary sm" disabled={!!busy || (!payCleared && !payReason.trim())}
                onClick={async () => {
                  setPayModal(false)
                  await act('pay', { action: 'payment-send', to: sendTo.trim(), cleared: payCleared, reason: payReason.trim() },
                    payCleared ? 'Payment recorded — report sent' : 'Report sent before payment',
                    payCleared ? {} : { detail: 'Your reason is recorded' })
                }}>{busy === 'pay' ? 'Sending…' : 'Confirm & send Audit Report'}</button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)}>
          <div className="modal" style={{ maxWidth: 880, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
              <h2 style={{ flex: 1 }}>{viewing.title}</h2>
              {viewing.reportId && <button className="sm" style={{ marginRight: 10 }} onClick={() => downloadReport(viewing.reportId)}>Download</button>}
              <button onClick={() => setViewing(null)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>
            <iframe src={viewing.url} title={viewing.title} style={{ width: '100%', height: '82vh', border: 'none' }} />
          </div>
        </div>)}
    </div>
  )
}

// Plain rows: element · clause per standard · who · remove. "+ Add row" / "Put back the standard list".
function PlanItemsEditor({ plan, standards, onChange, onReset }) {
  const items = plan.items || []
  const upd = (i, patch) => onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it))
  return (
    <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
      <div className="table-wrap"><table style={{ fontSize: 13 }}>
        <thead><tr><th>Element</th>{standards.map(s => <th key={s} style={{ width: 90 }}>{s.replace(/:\d+$/, '')}</th>)}<th style={{ width: 200 }}>Auditee</th><th style={{ width: 30 }}></th></tr></thead>
        <tbody>{items.map((it, i) => (
          <tr key={i}>
            {/* Text boxes (not single-line inputs) so the Word plans' multi-line elements keep their line breaks. */}
            <td><textarea rows={Math.min(6, String(it.element || '').split('\n').length)} value={it.element} onChange={e => upd(i, { element: e.target.value })} style={{ fontWeight: it.heading || it.bold ? 700 : 400, fontSize: 13 }} /></td>
            {standards.map((s, si) => <td key={s}>{!it.heading && <input value={(it.clauses || [])[si] || ''} onChange={e => { const c = [...(it.clauses || [])]; c[si] = e.target.value; upd(i, { clauses: c }) }} />}</td>)}
            <td>{!it.heading && <textarea rows={Math.min(4, String(it.who || '').split('\n').length)} value={it.who || ''} onChange={e => upd(i, { who: e.target.value })} style={{ fontSize: 13 }} />}</td>
            <td><button className="sm" title="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>×</button></td>
          </tr>))}
        </tbody>
      </table></div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="sm" onClick={() => onChange([...items, { element: '', clauses: standards.map(() => ''), who: 'FUNCTIONAL HEADS', heading: false }])}>+ Add row</button>
        <button className="sm" onClick={onReset}>Put back the standard list</button>
      </div>
    </div>
  )
}
